import { FC } from 'react';
import { Box, CircularProgress, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { FeatureFlagKey } from '@libs/constants';
import { type SingularKeys, useTrans } from '@libs/i18n';

import { useAdminFeatureFlags, useUpdateTenantFeatureFlag } from '../api/admin-api';

import { adminColors } from './adminTheme';

/** i18n key for each flag's human-facing label. Mirrors AdminFeaturesPage. */
const FLAG_LABEL_KEY: Record<FeatureFlagKey, SingularKeys> = {
  [FeatureFlagKey.REMEDIATION]: 'app.admin.features.flags.remediation',
  [FeatureFlagKey.ASSESSMENT_PROGRESS]: 'app.admin.features.flags.assessment_progress',
};

type OverrideValue = 'inherit' | 'on' | 'off';

/**
 * Per-organization override of every runtime feature flag. Three states per
 * flag: inherit the environment-wide default, or force it on/off for just
 * this tenant — for piloting a feature with one customer without changing
 * the default everyone else sees.
 */
export const TenantFeaturesTab: FC<{ tenantId: string }> = observer(({ tenantId }) => {
  const theme = useTheme();
  const c = adminColors(theme);
  const { t } = useTrans();
  const { data: flags, isLoading } = useAdminFeatureFlags();
  const updateTenantFeatureFlag = useUpdateTenantFeatureFlag();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const handleChange = (key: FeatureFlagKey, value: OverrideValue | null) => {
    if (value === null) return;
    updateTenantFeatureFlag.mutate({
      tenantId,
      key,
      enabled: value === 'inherit' ? null : value === 'on',
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {flags?.map((flag) => {
        const override = flag.overrides.find((o) => o.tenantId === tenantId);
        const value: OverrideValue = !override ? 'inherit' : override.enabled ? 'on' : 'off';

        return (
          <Box
            key={flag.key}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              p: 2,
              border: `1px solid ${c.border}`,
              borderRadius: 3,
              flexWrap: 'wrap',
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: c.ink }}>
                {t(FLAG_LABEL_KEY[flag.key])}
              </Typography>
              <Typography sx={{ fontSize: 12, color: c.sub, mt: 0.25 }}>
                {t('app.admin.features.tenantTab.inheritsFrom', {
                  state: flag.globalEnabled
                    ? t('app.admin.features.on')
                    : t('app.admin.features.off'),
                })}
              </Typography>
            </Box>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={value}
              onChange={(_, next) => handleChange(flag.key, next)}
              disabled={updateTenantFeatureFlag.isPending}
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontSize: 12.5,
                  px: 1.75,
                  py: 0.5,
                  color: c.sub,
                },
                '& .Mui-selected': {
                  color: `${c.ink} !important`,
                  backgroundColor: `${theme.palette.grey[200]} !important`,
                },
              }}
            >
              <ToggleButton value="inherit">{t('app.admin.features.tenantTab.inherit')}</ToggleButton>
              <ToggleButton value="on">{t('app.admin.features.forceOn')}</ToggleButton>
              <ToggleButton value="off">{t('app.admin.features.forceOff')}</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        );
      })}
    </Box>
  );
});
